<template>
  <div v-title="$route.meta.title"
       class="self-training-course-page">
    <div class="page-heading">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item>培训手册</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <el-card shadow="never"
             class="course-card">
      <div slot="header"
           class="course-card__header">
        <span class="course-card__title"><i class="el-icon-reading"></i>培训手册</span>
        <span class="course-card__summary">{{ summaryText }}</span>
      </div>

      <div v-loading="loading"
           class="course-list">
        <article v-for="course in courses"
                 :key="course.id"
                 class="course-item">
          <div class="course-item__main">
            <div :class="['course-item__icon', courseFiles(course).length > 1 ? 'is-folder' : fileTypeClass(courseFiles(course)[0])]">
              <i :class="courseFiles(course).length > 1 ? 'el-icon-folder-opened' : getFileIcon(courseFiles(course)[0])"></i>
            </div>
            <div class="course-item__content">
              <div class="course-item__title-row">
                <span class="course-item__title">{{ course.title }}</span>
                <span v-if="courseFiles(course).length > 1"
                      class="course-item__count">共 {{ courseFiles(course).length }} 个文件</span>
              </div>
              <div class="course-item__description">
                <template v-if="courseFiles(course).length > 1">
                  含 {{ courseFiles(course).length }} 个文件
                </template>
                <template v-else>
                  {{ (courseFiles(course)[0] || {}).name || '暂无文件' }}
                </template>
                · 发布人 {{ publisherName(course) }} · {{ course.created_at }}
              </div>
            </div>
          </div>

          <div class="course-item__actions">
            <template v-if="courseFiles(course).length > 1">
              <el-button size="mini"
                         plain
                         type="primary"
                         class="course-toggle"
                         @click="toggleCourse(course.id)">
                <i :class="isExpanded(course.id) ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                {{ isExpanded(course.id) ? '收起文件' : '展开文件' }}
              </el-button>
              <el-button size="mini"
                         plain
                         class="download-button"
                         @click="downloadAll(course)">
                <i class="el-icon-download"></i>下载全部
              </el-button>
            </template>
            <template v-else-if="courseFiles(course).length">
              <el-button v-if="isPreviewable((courseFiles(course)[0] || {}))"
                         size="mini"
                         plain
                         type="primary"
                         class="preview-button"
                         @click="openPreview(courseFiles(course)[0])">
                <i class="el-icon-view"></i>预览
              </el-button>
              <el-button size="mini"
                         plain
                         class="download-button"
                         :disabled="!courseFiles(course)[0].url"
                         @click="downloadFile(courseFiles(course)[0])">
                <i class="el-icon-download"></i>下载
              </el-button>
            </template>
          </div>

          <div v-if="courseFiles(course).length > 1 && isExpanded(course.id)"
               class="course-file-list">
            <div v-for="(file, index) in courseFiles(course)"
                 :key="file.uid || file.url || index"
                 class="course-file">
              <div class="course-file__name-wrap">
                <i :class="['course-file__icon', fileTypeClass(file), getFileIcon(file)]"></i>
                <span class="course-file__name"
                      :title="file.name">{{ file.name || '未命名文件' }}</span>
              </div>
              <span class="course-file__size">{{ formatFileSize(file.size) }}</span>
              <span class="course-file__date">{{ file.submit_at || course.created_at }}</span>
              <div class="course-file__actions">
                <el-button v-if="isPreviewable(file)"
                           size="mini"
                           plain
                           type="primary"
                           class="preview-button"
                           @click="openPreview(file)">
                  <i class="el-icon-view"></i>预览
                </el-button>
                <el-button size="mini"
                           plain
                           class="download-button"
                           :disabled="!file.url"
                           @click="downloadFile(file)">
                  <i class="el-icon-download"></i>下载
                </el-button>
              </div>
            </div>
          </div>
        </article>

        <div v-if="!loading && loadFailed"
             class="manual-empty">
          <p>培训手册加载失败</p>
          <el-button class="manual-retry"
                     size="small"
                     plain
                     type="primary"
                     @click="fetchCourses">重新加载
          </el-button>
        </div>
        <div v-else-if="!loading && !courses.length"
             class="manual-empty">
          <i class="el-icon-reading"></i>
          <p>暂无培训手册</p>
        </div>
      </div>

      <el-pagination v-if="total > pageSize"
                     class="course-pagination"
                     background
                     layout="prev, pager, next, total"
                     :current-page="currentPage"
                     :page-size="pageSize"
                     :total="total"
                     :disabled="loading"
                     @current-change="changePage"/>
    </el-card>

    <el-dialog :visible.sync="previewVisible"
               :title="previewFile ? previewFile.name : '培训文件预览'"
               width="80%"
               top="5vh"
               append-to-body
               custom-class="self-training-preview-dialog"
               @close="previewFile = null">
      <iframe v-if="previewFile && previewType === 'pdf'"
              class="course-preview__pdf"
              :src="previewFile.url"
              :title="previewFile.name"></iframe>
      <video v-else-if="previewFile && previewType === 'video'"
             class="course-preview__video"
             :src="previewFile.url"
             controls
             autoplay></video>
      <el-image v-else-if="previewFile && previewType === 'image'"
                class="course-preview__image"
                fit="contain"
                :src="previewFile.url"
                :preview-src-list="[previewFile.url]"/>
    </el-dialog>
  </div>
</template>

<script>
import { getCourseFileTypeByFile } from '@views/training/training-course-intern.constants'

const PREVIEW_FILE_TYPES = ['img', 'video', 'pdf']

export default {
  name: 'SelfTrainingCourseTable',
  data () {
    return {
      loading: false,
      loadFailed: false,
      courses: [],
      total: 0,
      currentPage: 1,
      pageSize: 20,
      expandedCourses: {},
      previewVisible: false,
      previewFile: null,
    }
  },
  computed: {
    summaryText () {
      if (this.total === this.courses.length) {
        return `共 ${this.total} 本手册 · 共 ${this.fileCount} 个文件`
      }
      return `共 ${this.total} 本手册 · 本页 ${this.fileCount} 个文件`
    },
    fileCount () {
      return this.courses.reduce((count, course) => count + this.courseFiles(course).length, 0)
    },
    previewType () {
      const type = getCourseFileTypeByFile(this.previewFile)
      return type === 'img' ? 'image' : type
    },
  },
  created () {
    this.fetchCourses()
  },
  methods: {
    fetchCourses () {
      this.loading = true
      this.loadFailed = false
      return request.common('getSelfTrainingCourses', {
        current_page: this.currentPage,
        per_page: this.pageSize,
      }).then(res => {
        const data = res && res.data ? res.data : {}
        this.courses = Array.isArray(data.data) ? data.data : []
        this.total = Number(data.total) || 0
        this.currentPage = Number(data.current_page) || this.currentPage
        this.pageSize = Number(data.per_page) || this.pageSize
      }).catch(() => {
        this.loadFailed = true
      }).finally(() => {
        this.loading = false
      })
    },
    changePage (page) {
      this.currentPage = page
      this.courses = []
      this.expandedCourses = {}
      return this.fetchCourses()
    },
    courseFiles (course) {
      return course && Array.isArray(course.course_json)
        ? course.course_json.filter(file => file && typeof file === 'object')
        : []
    },
    publisherName (course) {
      const firstFile = this.courseFiles(course)[0] || {}
      return course.user_name || firstFile.submit_admin_name || '—'
    },
    formatFileSize (size) {
      const bytes = Number(size)
      if (!bytes || bytes < 0) return '—'
      if (bytes < 1024) return `${bytes}B`
      if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
    },
    getFileIcon (file) {
      const type = getCourseFileTypeByFile(file)
      const icons = {
        img: 'el-icon-picture-outline',
        video: 'el-icon-video-camera',
        pdf: 'el-icon-document',
        ppt: 'el-icon-document',
        word: 'el-icon-document',
        excel: 'el-icon-document',
      }
      return icons[type] || 'el-icon-document'
    },
    fileTypeClass (file) {
      return `is-${getCourseFileTypeByFile(file) || 'file'}`
    },
    isPreviewable (file) {
      return !!(file && file.url && PREVIEW_FILE_TYPES.includes(getCourseFileTypeByFile(file)))
    },
    isExpanded (courseId) {
      return !!this.expandedCourses[courseId]
    },
    toggleCourse (courseId) {
      this.$set(this.expandedCourses, courseId, !this.isExpanded(courseId))
    },
    openPreview (file) {
      if (!this.isPreviewable(file)) return
      this.previewFile = file
      this.previewVisible = true
    },
    downloadFile (file) {
      if (!file || !file.url) return
      if (!this.isDownloadableUrl(file.url)) {
        this.$message.error(`${file.name || '文件'}下载地址无效`)
        return
      }
      window.open(file.url, '_blank', 'noopener')
    },
    isDownloadableUrl (url) {
      try {
        const link = document.createElement('a')
        link.href = url
        return ['http:', 'https:'].includes(link.protocol)
      } catch (error) {
        return false
      }
    },
    downloadAll (course) {
      this.courseFiles(course).forEach(file => this.downloadFile(file))
    },
  },
}
</script>

<style lang="scss"
       scoped>
.course-card {
  border-radius: 10px;

  ::v-deep .el-card__header {
    padding: 16px 20px;
  }

  ::v-deep .el-card__body {
    padding: 0 20px;
  }
}

.course-card__header,
.course-item,
.course-item__main,
.course-item__actions,
.course-item__title-row,
.course-file,
.course-file__name-wrap,
.course-file__actions {
  display: flex;
  align-items: center;
}

.course-card__header,
.course-item {
  justify-content: space-between;
}

.course-card__title {
  color: #303133;
  font-size: 15px;
  font-weight: 600;

  i {
    margin-right: 8px;
    color: #409eff;
  }
}

.course-card__summary,
.course-item__description,
.course-file__size,
.course-file__date {
  color: #909399;
  font-size: 12px;
}

.course-item {
  flex-wrap: wrap;
  gap: 14px 20px;
  padding: 16px 0;
  border-bottom: 1px solid #f0f2f5;
}

.course-item:last-child {
  border-bottom: 0;
}

.course-item__main {
  flex: 1;
  min-width: 220px;
  gap: 14px;
}

.course-item__icon {
  display: flex;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #f2f7ff;
  color: #409eff;
  font-size: 18px;

  &.is-folder {
    background: #eef5ff;
    color: #409eff;
  }
}

.course-item__content {
  min-width: 0;
}

.course-item__title-row {
  flex-wrap: wrap;
  gap: 8px;
}

.course-item__title {
  overflow: hidden;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.course-item__count {
  padding: 3px 6px;
  border-radius: 3px;
  background: #eff6ff;
  color: #409eff;
  font-size: 11px;
}

.course-item__description {
  margin-top: 5px;
}

.course-item__actions,
.course-file__actions {
  flex-shrink: 0;
  gap: 8px;

  .el-button + .el-button {
    margin-left: 0;
  }
}

.course-file-list {
  display: grid;
  width: 100%;
  gap: 6px;
  padding-left: 52px;
}

.course-file {
  min-height: 40px;
  gap: 16px;
  padding: 5px 10px;
  border-radius: 6px;
  background: #f7f8fa;
}

.course-file__name-wrap {
  flex: 1;
  min-width: 100px;
  gap: 8px;
}

.course-file__icon {
  flex: 0 0 auto;
  color: #409eff;
}

.course-item__icon,
.course-file__icon {
  &.is-img,
  &.is-excel {
    color: #20b26b;
  }

  &.is-video {
    color: #8b5cf6;
  }

  &.is-pdf {
    color: #f56c6c;
  }
}

.course-file__name {
  overflow: hidden;
  color: #303133;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.course-file__date {
  flex: 0 0 auto;
}

.course-pagination {
  padding: 18px 0;
  text-align: right;
}

.manual-empty {
  padding: 36px 0;
  color: #909399;
  text-align: center;

  i {
    font-size: 36px;
    color: #c0c4cc;
  }
}

.course-preview__pdf,
.course-preview__video {
  display: block;
  width: 100%;
  height: 72vh;
  border: 0;
}

.course-preview__video {
  background: #111;
}

.course-preview__image {
  display: block;
  width: 100%;
  height: 72vh;
}

@media (max-width: 768px) {
  .course-item__main {
    min-width: 100%;
  }

  .course-item__actions {
    padding-left: 52px;
  }

  .course-file-list {
    padding-left: 0;
  }

  .course-file {
    flex-wrap: wrap;
    gap: 8px 12px;
  }

  .course-file__name-wrap {
    flex-basis: 100%;
  }
}
</style>

<style lang="scss">
.self-training-preview-dialog {
  .el-dialog__body {
    padding: 12px 20px 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/training/pages/course/SelfTrainingCourseTable.vue